import { OmrUploadStatusEnum } from "@/enums/conductTest";
import { PreloadedContentCategoriesEnum } from "@/enums/content";
import { AssignmentStatusEnum } from "@/enums/createAssignment";
import { InstituteTestStatusEnum } from "@/enums/createTest";
import { LiveClassCategoryEnum } from "@/enums/liveClass";
import { LivePracticeCategoryEnum } from "@/enums/livePractice";
import { FilterableStoreIdsEnum, PaginatedStoreIdsEnum } from "@/enums/stores";
import {
  DemoSessionStatusIdEnum,
  TutorSessionCategoryEnum,
  ValidTutorSessionCategoryEnum,
} from "@/enums/tutoring";
import { getIdNamePairsFromEnum } from "@/methods/utils";

export const PaginatedStoreSettings = Object.freeze({
  [PaginatedStoreIdsEnum.CREATE_PRELOADED_CONTENT]: {
    CategoryTabs: getIdNamePairsFromEnum(PreloadedContentCategoriesEnum),
    SearchFields: ["Content", "Course", "Subjects", "CreatedBy", "UpdatedBy"],
    PlaceholderText: "Search by name, course, subject...",
    UiPageSize: 10,
    ApiPageSize: 200,
    EnableDownload: false,
    DownloadItems: null,
  },
  [PaginatedStoreIdsEnum.MANAGE_AFFILIATIONS]: {
    CategoryTabs: null,
    SearchFields: ["Affiliation", "EmailId", "Mobile"],
    PlaceholderText: "Search by affiliation, email, mobile",
    UiPageSize: 10,
    ApiPageSize: 200,
    EnableDownload: false,
    DownloadItems: null,
  },
  [PaginatedStoreIdsEnum.MANAGE_ASSIGNMENTS]: {
    CategoryTabs: getIdNamePairsFromEnum(AssignmentStatusEnum),
    SearchFields: ["AssignmentName", "Course", "Subjects", "Batches", "CreatedBy", "UpdatedBy"],
    PlaceholderText: "Search by assignment name, subject, batch...",
    UiPageSize: 10,
    ApiPageSize: 200,
    EnableDownload: false,
    DownloadItems: null,
  },
  [PaginatedStoreIdsEnum.MANAGE_INSTITUTE_TESTS]: {
    CategoryTabs: getIdNamePairsFromEnum(InstituteTestStatusEnum),
    SearchFields: ["TestName", "Course", "Subjects", "Batches", "CreatedBy", "UpdatedBy"],
    PlaceholderText: "Search by test name, subject, batch...",
    UiPageSize: 10,
    ApiPageSize: 200,
    EnableDownload: false,
    DownloadItems: null,
  },
  [PaginatedStoreIdsEnum.MANAGE_LIVE_CLASSES]: {
    CategoryTabs: getIdNamePairsFromEnum(LiveClassCategoryEnum),
    SearchFields: ["LiveClassName", "Subject", "Batches", "Faculty"],
    PlaceholderText: "Search by class name, subject, batch...",
    UiPageSize: 10,
    ApiPageSize: 200,
    EnableDownload: true,
    CardsDownloadItems: [
      "LiveClassName",
      "Faculty.Name",
      "Subject.Name",
      "StartDatetime",
      "Duration",
      "MeetingAttendeeUrl",
      "OwnerEmailId",
    ],
  },
  [PaginatedStoreIdsEnum.MANAGE_LIVE_PRACTICE]: {
    CategoryTabs: getIdNamePairsFromEnum(LivePracticeCategoryEnum),
    SearchFields: ["LivePracticeName", "Subject", "Moderator", "Batches", "Moderator"],
    PlaceholderText: "Search by practice name, subject, batch...",
    UiPageSize: 10,
    ApiPageSize: 200,
    EnableDownload: false,
    DownloadItems: null,
  },
  [PaginatedStoreIdsEnum.MANAGE_QR_CODES]: {
    CategoryTabs: null,
    SearchFields: [
      "QrCategory",
      "QrTitle",
      "QrDescription",
      "AdditionalInfo",
      "QrTarget",
      "Content",
    ],
    PlaceholderText: "Search by title, description, additional info...",
    UiPageSize: 10,
    ApiPageSize: 200,
    EnableDownload: false,
    DownloadItems: null,
  },
  [PaginatedStoreIdsEnum.MANAGE_SCHOOLS]: {
    CategoryTabs: null,
    SearchFields: ["SchoolName", "Address", "Spoc", "Csm", "Kdm"],
    PlaceholderText: "Search by school name, address, spoc, csm, kdm...",
    UiPageSize: 10,
    ApiPageSize: 0,
    EnableDownload: false,
    DownloadItems: null,
  },
  [PaginatedStoreIdsEnum.MANAGE_USERS]: {
    CategoryTabs: null,
    SearchFields: ["UserName", "EmailId", "Mobile"],
    PlaceholderText: "Search by user name, email, mobile",
    UiPageSize: 10,
    ApiPageSize: 50,
    EnableDownload: false,
    DownloadItems: null,
  },
  [PaginatedStoreIdsEnum.OFFLINE_TEST_SUBJECT_WISE_REPORT]: {
    CategoryTabs: null,
    SearchFields: ["Affiliation", "Batch", "TestName"],
    PlaceholderText: "Search by test, affiliation, batch...",
    UiPageSize: 10,
    ApiPageSize: 200,
    EnableDownload: false,
    DownloadItems: null,
  },
  [PaginatedStoreIdsEnum.TUTOR_SESSION_REPORTS]: {
    CategoryTabs: null,
    SearchFields: ["Student", "Course", "Module"],
    PlaceholderText: "Search by student, course, module",
    UiPageSize: 10,
    ApiPageSize: 50,
    EnableDownload: false,
    DownloadItems: null,
  },
  [PaginatedStoreIdsEnum.TUTORING_ADMIN_DEMO_SESSIONS]: {
    CategoryTabs: getIdNamePairsFromEnum(DemoSessionStatusIdEnum),
    SearchFields: ["SessionTitle", "Student", "Tutor", "Course"],
    PlaceholderText: "Search by title, student, tutor, course...",
    UiPageSize: 10,
    ApiPageSize: 500,
    EnableDownload: false,
    DownloadItems: null,
  },
  [PaginatedStoreIdsEnum.TUTORING_ADMIN_PAID_SESSIONS]: {
    CategoryTabs: getIdNamePairsFromEnum(TutorSessionCategoryEnum),
    SearchFields: ["SessionTitle", "Student", "Tutor", "Course"],
    PlaceholderText: "Search by title, student, tutor, course...",
    UiPageSize: 10,
    ApiPageSize: 500,
    EnableDownload: false,
    DownloadItems: null,
  },
  [PaginatedStoreIdsEnum.TUTORING_STUDENT_DASHBOARD]: {
    CategoryTabs: null,
    SearchFields: ["SessionTitle", "Course"],
    PlaceholderText: "Search by title, course...",
    UiPageSize: 10,
    ApiPageSize: 200,
    EnableDownload: false,
    DownloadItems: null,
  },
  [PaginatedStoreIdsEnum.TUTORING_TUTOR_DASHBOARD]: {
    CategoryTabs: getIdNamePairsFromEnum(ValidTutorSessionCategoryEnum),
    SearchFields: ["SessionTitle", "Course", "Student"],
    PlaceholderText: "Search by title, course, student...",
    UiPageSize: 10,
    ApiPageSize: 50,
    EnableDownload: false,
    DownloadItems: null,
  },
});

export const FilterableStoreSettings = Object.freeze({
  [FilterableStoreIdsEnum.MANAGE_BATCHES]: {
    CategoryTabs: null,
    SearchFields: ["Course", "AcademicSession", "Batch", "Course", "Class"],
    PlaceholderText: "Search by course, session, batch, course...",
    UiPageSize: 10,
    IncludeDateFilter: false,
    DateRangeDays: null,
    EnableDownload: false,
    DownloadItems: null,
  },
  [FilterableStoreIdsEnum.MANAGE_OMR_UPLOADS]: {
    CategoryTabs: getIdNamePairsFromEnum(OmrUploadStatusEnum),
    SearchFields: ["Affiliation", "Batch", "InstituteTest"],
    PlaceholderText: "Search by affiliation, batch, institute test",
    UiPageSize: 10,
    IncludeDateFilter: true,
    DateRangeDays: 90,
    EnableDownload: false,
    DownloadItems: null,
  },
  [FilterableStoreIdsEnum.MANAGE_PRELOADED_CONTENT]: {
    CategoryTabs: getIdNamePairsFromEnum(PreloadedContentCategoriesEnum),
    SearchFields: ["Content", "Course", "Subjects"],
    PlaceholderText: "Search by name, course, subject...",
    UiPageSize: 10,
    IncludeDateFilter: false,
    DateRangeDays: null,
    EnableDownload: false,
    DownloadItems: null,
  },
  [FilterableStoreIdsEnum.MANAGE_SCHOOLS]: {
    CategoryTabs: null,
    SearchFields: ["SchoolName", "Address", "Spoc", "Csm", "Kdm"],
    PlaceholderText: "Search by name, address, SPOC,...",
    UiPageSize: 10,
    IncludeDateFilter: false,
    DateRangeDays: null,
    EnableDownload: false,
    DownloadItems: null,
  },
  [FilterableStoreIdsEnum.MANAGE_SCHOOL_USERS]: {
    CategoryTabs: null,
    SearchFields: ["Schools", "UserName", "Mobile", "EmailId"],
    PlaceholderText: "Search by name, mobile, email, school",
    UiPageSize: 10,
    IncludeDateFilter: false,
    DateRangeDays: null,
    EnableDownload: false,
    DownloadItems: null,
  },
  [FilterableStoreIdsEnum.MANAGE_TUTORING_DEMO_STUDENTS]: {
    CategoryTabs: null,
    SearchFields: ["Student", "Course"],
    PlaceholderText: "Search by student, course...",
    IncludeDateFilter: true,
    DateRangeDays: 90,
    UiPageSize: 10,
    EnableDownload: false,
    DownloadItems: null,
  },
  [FilterableStoreIdsEnum.MANAGE_TUTORING_PAID_STUDENTS]: {
    CategoryTabs: null,
    SearchFields: ["Student", "Course"],
    PlaceholderText: "Search by student, course...",
    IncludeDateFilter: true,
    DateRangeDays: 90,
    UiPageSize: 10,
    EnableDownload: false,
    DownloadItems: null,
  },
  [FilterableStoreIdsEnum.THE_SEARCH_USERS_BY_TEXT]: {
    CategoryTabs: null,
    SearchFields: ["UserName", "EmailId", "Mobile"],
    PlaceholderText: "Search by name, email, mobile",
    UiPageSize: 10,
    IncludeDateFilter: false,
    DateRangeDays: null,
    EnableDownload: false,
    DownloadItems: null,
  },
  [FilterableStoreIdsEnum.TUTORING_MONTHLY_REPORT]: {
    CategoryTabs: null,
    SearchFields: ["ParentName", "Student", "Course"],
    PlaceholderText: "Search by student, parent, course",
    UiPageSize: 10,
    IncludeDateFilter: false,
    DateRangeDays: null,
    EnableDownload: true,
    DownloadItems: [
      "Tutor.Name",
      "Student.Name",
      "ParentName",
      "Parent.Email",
      "Parent.Mobile",
      "Course.Name",
      "StartDatetime",
      "PricePerClass",
    ],
  },
  [FilterableStoreIdsEnum.TUTORING_STUDENT_STUDY_NOTES]: {
    CategoryTabs: null,
    SearchFields: ["ModuleName", "ModuleDescription"],
    PlaceholderText: "Search by module name, description",
    UiPageSize: 10,
    IncludeDateFilter: false,
    DateRangeDays: null,
    EnableDownload: false,
    DownloadItems: null,
  },
  [FilterableStoreIdsEnum.TUTORING_TUTOR_LESSON_PLANS]: {
    CategoryTabs: null,
    SearchFields: ["ModuleName", "ModuleDescription"],
    PlaceholderText: "Search by module name, description",
    UiPageSize: 10,
    IncludeDateFilter: false,
    DateRangeDays: null,
    EnableDownload: false,
    DownloadItems: null,
  },
  [FilterableStoreIdsEnum.TUTOR_UNASSIGNED_DEMOS]: {
    CategoryTabs: null,
    SearchFields: ["Student", "Course", "SessionTitle"],
    PlaceholderText: "Search by student, course, title",
    UiPageSize: 10,
    IncludeDateFilter: false,
    DateRangeDays: null,
    EnableDownload: false,
    DownloadItems: null,
  },
});
